import { FC, useRef, useState } from "react";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { useCostTypesApi } from "../../../http/costTypes/api";
import { CostType } from "../../../http/costTypes/types";
import { NestedPick, PaginationQueryWithFields } from "../../../types";
import { isWithinLast48h } from "../../../types/utils";
import Button from "../../ui/Button";
import CustomLink from "../../ui/CustomLink";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../../ui/ListItemMenu";
import ModalDialog from "../../ui/ModalDialog";
import NewItemIcon from "../../ui/NewItemIcon";
import Table from "../../ui/Table";
import TextColumnFilter from "../../ui/columnFilters/TextColumnFilter";
import CostTypeCategories from "../typeCategories/CostTypeCategories";
import EditCostTypeCategoryDialog from "../typeCategories/EditCategoryButton";
import styles from "./CostTypes.module.css";
import CostTypesHistoryDialog from "./CostTypesHistoryDialog";
import CreateCostTypeButton from "./CreateCostTypeButton";
import DeleteCostTypeDialog from "./DeleteCostTypeDialog";
import EditCostTypeDialog from "./EditCostTypeDialog";

const COST_TYPES_FIELDS = [
  "id",
  "createdAt",
  "name",
  "category.id",
  "category.name",
  "dkvProductCodes",
  "pkwAccountingNumber",
  "lkwAccountingNumber",
  "pkwSuperiorAccountingNumber",
  "costObject",
  "versionNumber",
] as const;
type CostTypeField = (typeof COST_TYPES_FIELDS)[number];
type CostTypeSearch = NestedPick<CostType, CostTypeField>;

const formatDkvProductCodes = (values?: string[]) => values?.join(", ") ?? "";

interface CostTypesProps {
  onClose: () => void;
}

const CostTypes: FC<CostTypesProps> = ({ onClose }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true, prefix: "ct", limit: 15 });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const costTypesApi = useCostTypesApi();

  const [itemToDelete, setItemToDelete] = useState<CostTypeSearch>();
  const [itemToEdit, setItemToEdit] = useState<CostTypeSearch>();
  const [showCostTypeCategories, setShowCostTypeCategories] = useState(false);
  const [itemToShowVersions, setItemToShowVersions] = useState<CostTypeSearch>();
  const [showCategoryId, setShowCategoryId] = useState<string>();

  const query: PaginationQueryWithFields<CostTypeSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: COST_TYPES_FIELDS,
  };

  const result = costTypesApi.useAdminCostTypes(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: costTypesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: CostTypeSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.costTypes.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.costTypes.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <ModalDialog onClose={onClose} title={i18n.t.costTypes.plural} className={styles["cost-types"]}>
        <Table<CostTypeSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          buttons={{
            main: userSettings.permissions.costTypes.insert && <CreateCostTypeButton />,
            secondary: userSettings.permissions.costTypeCategories.read && (
              <Button
                buttonProps={{
                  onClick: () => setShowCostTypeCategories(true),
                }}
              >
                {i18n.t.common.manageCostTypeCategories}
              </Button>
            ),
          }}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="type.name" order={orderOptions} />
              <Table.Th
                title={i18n.t.common.category}
                fieldName="type.category.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.dkvProductCodes}
                fieldName="type.dkv.productCodes"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.pkwAccountingNumberShort}
                fieldName="type.pkw"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.lkwAccountingNumberShort}
                fieldName="type.lkw"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.pkwSuperiorAccountingNumberShort}
                fieldName="type.pkwSuperior"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.costObject}
                fieldName="type.costObject"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.name}
              </td>
              <td>
                {userSettings.permissions.costTypeCategories.read ? (
                  <CustomLink to="" onClick={() => setShowCategoryId(item.category.id)}>
                    {item.category.name}
                  </CustomLink>
                ) : (
                  item.category.name
                )}
              </td>
              <td>{formatDkvProductCodes(item.dkvProductCodes)}</td>
              <td>{item.pkwAccountingNumber}</td>
              <td>{item.lkwAccountingNumber}</td>
              <td>{item.pkwSuperiorAccountingNumber}</td>
              <td>{item.costObject}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </ModalDialog>

      {!!itemToDelete && (
        <DeleteCostTypeDialog costTypeId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToEdit && <EditCostTypeDialog costTypeId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />}

      {showCostTypeCategories && <CostTypeCategories onClose={() => setShowCostTypeCategories(false)} />}

      {!!itemToShowVersions && (
        <CostTypesHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!showCategoryId && (
        <EditCostTypeCategoryDialog costTypeCategoryId={showCategoryId} onClose={() => setShowCategoryId(undefined)} />
      )}
    </>
  );
};

export default CostTypes;
